<template>
  <div class="openToMove">
    <b-container fluid class="ui-container">
      <div class="ui-container__header">
        <h1>{{ this.$t(`openToMove.title`) }}</h1>
      </div>

      <div class="d-flex justify-content-between">
        <div class="d-flex">
          <div class="d-flex flex-column">
            <label class="font-weight-bold">{{ $t("bookingTransport.planning.TablePlanning.headings.period_from")
              }}</label>
            <date-picker class="dateInput" v-model="dates.from" placeholder="YYYY/MM/DD" value-type="format"
              format="YYYY-MM-DD">
            </date-picker>
            <div v-if="$v.dates.from.$error" class="ui-input-error-msg">
              <p v-if="!$v.dates.from.required">{{ $t("bookingTransport.planning.TablePlanning.headings.period_from") }}
                {{ $t("global.inputs.required") }}</p>
            </div>
          </div>

          <div class="d-flex flex-column ml-4">
            <label class="font-weight-bold">{{ $t("bookingTransport.planning.TablePlanning.headings.period_to")
              }}</label>
            <date-picker class="dateInput" v-model="dates.to" placeholder="YYYY/MM/DD" value-type="format"
              format="YYYY-MM-DD">
            </date-picker>
            <div v-if="$v.dates.to.$error" class="ui-input-error-msg">
              <p v-if="!$v.dates.to.required">{{ $t("bookingTransport.planning.TablePlanning.headings.period_to") }}
                {{ $t("global.inputs.required") }}</p>
            </div>
          </div>
        </div>

        <download-excel class="ui-action-button" :fetch="fetchData" :fields="json_fields"
          :before-finish="finishDownload" :aria-modal="json_meta" type="csv" worksheet="My Worksheet"
          name="OpenToMove.xls">
          {{ $t('openToMove.dowloadReport') }}
        </download-excel>
      </div>

      <div class="d-flex w-100 mt-4">
        <button v-if="selectedItems.length" class="ui-action-button" slot="beforeLimit"
          @click="openMassiveConfirmationModal">
          {{ $t('openToMove.Notification') }}
        </button>
      </div>

      <div class="row">
        <div class="col-md-12">
          <v-server-table class="ui-table" :columns="table_open_to_move.columns" :options="table_open_to_move.options"
            ref="TableOpenToMove" id="TableOpenToMove">
            <template v-slot:action="props">
              <div class="ui-table__action-button"><i class='fas fas fa-edit' @click="openEditModal(props.row)"></i>
              </div>
            </template>
            <template v-slot:resume="props">
              <div class="ui-table__action-button" v-if="props.row.resume">
                <i class='fas' @click="Download(props.row.resume)">&#xf56d;</i>
              </div>
            </template>
            <template v-slot:license="props">
              <div class="ui-table__action-button" v-if="props.row.license">
                <i class='fas' @click="Download(props.row.license)">&#xf56d;</i>
              </div>
            </template>
            <template #createdAt="props">{{ props.row.createdAt | formatDateOnly(appLang) }}</template>
            <template #contractEndDate="props">{{ props.row.contractEndDate | formatContractEndDate }}</template>
            <template #licenseType="props">{{ getLicenseType(props.row.licenseType) }} </template>
            <template #status="props">{{ getStatusName(props.row.status) }} </template>
            <template #languages="props">
              <span v-html="getLanguages(props.row.languages, true)"></span>
            </template>
            <template #currentExperience="props">
              <span v-html="getExperience(props.row.currentExperience, true)"></span>
            </template>

            <template #internationalMobility="props">{{ props.row.internationalMobility ? $t('global.yes') :
              $t('global.no') }}</template>

            <template v-slot:reasons="props">
              <div class="wrapped-text">
                {{ props.row.reasons }}
              </div>
            </template>

            <div slot="filter__status">
              <select class="form-control" v-model="headingSelectorValues.status"
                @change="filterByColumn(openToMoveConfig.v1.columns.status, headingSelectorValues.status)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in statusList" :key="option.key" :value="option.key">
                  {{ option.name }}
                </option>
              </select>
            </div>

            <div slot="filter__licenseType">
              <select class="form-control" v-model="headingSelectorValues.licenseType"
                @change="filterByColumn(openToMoveConfig.v1.columns.licenseType, headingSelectorValues.licenseType)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in openToMoveLicenseType" :key="option.key" :value="option.key">
                  {{ option.name }}
                </option>
              </select>
            </div>

            <div slot="filter__internationalMobility">
              <select class="form-control" v-model="headingSelectorValues.mobilityColumn"
                @change="filterByColumn(openToMoveConfig.v2.columns.internationalMobility, headingSelectorValues.mobilityColumn)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in booleanOptions" v-bind:key="option.text" v-bind:value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div slot="filter__employeeType">
              <select class="form-control" v-model="headingSelectorValues.employeeType"
                @change="filterByColumn(openToMoveConfig.v2.columns.employeeType, headingSelectorValues.employeeType)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in openToMoveEmployeeTypeOptions" v-bind:key="option.text"
                  v-bind:value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div slot="filter__contractType">
              <select class="form-control" v-model="headingSelectorValues.contractType"
                @change="filterByColumn(openToMoveConfig.v2.columns.contractType, headingSelectorValues.contractType)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in openToMoveContractTypeOptions" :key="option.text" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div slot="filter__lengthEmployment">
              <select class="form-control" v-model="headingSelectorValues.lengthEmployment"
                @change="filterByColumn(openToMoveConfig.v2.columns.lengthEmployment, headingSelectorValues.lengthEmployment)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in lengthEmploymentOptions" :key="option.text" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div slot="filter__languages">
              <select class="form-control" v-model="headingSelectorValues.languages"
                @change="filterByColumn(openToMoveConfig.v2.columns.languages, headingSelectorValues.languages)">
                <option value="">{{ $t('global.select') }} </option>
                <option v-for="option in languageOptions" :key="option.text" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <template v-slot:checkbox="props">
              <input v-if="props.row.status == 'delivered'" type="checkbox" :name="`select${props.row.id}`"
                :id="`select${props.row.id}`" :value="props.row" v-model="selectedItems"
                @click="checkMassive(props.row, $event)" />
            </template>
          </v-server-table>
        </div>
      </div>
    </b-container>

    <b-modal style="z-index:-1" size="xl" centered ref="Edit_Open_to_move" no-close-on-backdrop tabindex="-1">
      <template #modal-header>
        <div class="w-100">
          <b-row>
            <b-col sm="6">
              <h1>{{ $t('openToMove.application.detail.title') }}</h1>
              <div class="openToMove__application-modal--title">
                <label class="font-weight-bold">{{ $t(`openToMove.application.tableOpenToMove.headings.createdAt`)
                  }}</label>
                <input type="text" :value="[currentItem.parsedCreatedAt]" disabled />
              </div>
            </b-col>
            <b-col sm="3" md="6" lg="3">
              <h3>{{ $t("openToMove.application.tableOpenToMove.headings.status") }}</h3>
              <select class="form-control" v-model="currentItem.status">
                <option value="" disabled>{{ $t('global.select') }}</option>
                <option v-for="data in statusList" :key="data.key" :value="data.key" v-text="data.name"></option>
              </select>
            </b-col>
            <b-col sm="3" md="3" lg="3">
              <div class="w-100 ui-align-end">
                <b-button class="ui-action-button" @click="changeStatus()">
                  {{ $t('global.confirm') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>
      </template>
      <b-container>
        <b-row v-if="currentItem && Object.keys(currentItem).length">
          <b-col :sm="field.columns[currentItem.version].sm" :key="`detail-field-${field.key}`"
            v-for="field in detailFields">
            <div :class="`d-flex flex-column mt-4`">
              <label class="font-weight-bold">{{ $t(`openToMove.application.tableOpenToMove.headings.${field.key}`)
                }}</label>

              <textarea v-if="field.type === 'textarea'" :class="`form-control no-resize ${field.class}`"
                :value="currentItem[field.key]" rows="5" disabled></textarea>

              <div v-if="field.type === 'link'">
                <a v-if="currentItem[field.key]" target="_blank" :href="currentItem[field.key]">{{ $t('global.link')
                  }}</a>
                <input disabled v-else :placeholder="$t('global.none')" />
              </div>

              <input v-if="!field.type" type="text" :value="currentItem[field.key]" disabled />
            </div>
          </b-col>
        </b-row>
      </b-container>

      <template #modal-footer>
        <div class="w-100 ui-align-end">
          <button class="ui-cancel-button" @click="closeEditRequest()">
            {{ $t("global.cancel") }}
          </button>
        </div>
      </template>
    </b-modal>

    <b-modal style="z-index:1" size="xl" centered ref="my-notification-massive" no-close-on-backdrop tabindex="-1">
      <template #modal-header>
        <h1>{{ $t('openToMove.continueConfirm') }}</h1>
      </template>
      <b-container>
        <h3 class="ui-error-color">{{ $t("openToMove.warningNot") }}</h3>
        <b-row>
          <b-col sm="12">
            <v-client-table class="ui-table" :data="selectedItems" :columns="table_open_to_move.columns2ndTable"
              :options="table_open_to_move.options" ref="AddNotMassive" id="AddNotMassive">
              <template v-slot:resume="props">
                <div class="ui-table__action-button"><i class='fas' @click="Download(props.row.resume)">&#xf56d;</i>
                </div>
              </template>
              <template v-slot:license="props">
                <div class="ui-table__action-button"><i class='fas' @click="Download(props.row.license)">&#xf56d;</i>
                </div>
              </template>
              <template #createdAt="props">{{ props.row.createdAt | formatDateOnly(appLang) }}</template>
              <template #licenseType="props">{{ getLicenseType(props.row.licenseType) }}</template>
              <template #status="props">{{ getStatusName(props.row.status) }}</template>
              <template #languages="props">
                <span v-html="getLanguages(props.row.languages, true)"></span>
              </template>
              <template #currentExperience="props">
                <span v-html="getExperience(props.row.currentExperience, true)"></span>
              </template>

              <template #internationalMobility="props">{{ props.row.internationalMobility ? $t('global.yes') :
                $t('global.no') }} </template>


              <div slot="filter__status">
                <select class="form-control" v-model="headingSelectorValues.status"
                  @change="filterByColumn(openToMoveConfig.v1.columns.status, headingSelectorValues.status)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in statusList" :key="option.key" :value="option.key">
                    {{ option.name }}
                  </option>
                </select>
              </div>

              <div slot="filter__licenseType">
                <select class="form-control" v-model="headingSelectorValues.licenseType"
                  @change="filterByColumn(openToMoveConfig.v1.columns.licenseType, headingSelectorValues.licenseType)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in openToMoveLicenseType" :key="option.key" :value="option.key">
                    {{ option.name }}
                  </option>
                </select>
              </div>

              <div slot="filter__internationalMobility">
                <select class="form-control" v-model="headingSelectorValues.mobilityColumn"
                  @change="filterByColumn(openToMoveConfig.v2.columns.internationalMobility, headingSelectorValues.mobilityColumn)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in booleanOptions" v-bind:key="option.text" v-bind:value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

              <div slot="filter__employeeType">
                <select class="form-control" v-model="headingSelectorValues.employeeType"
                  @change="filterByColumn(openToMoveConfig.v2.columns.employeeType, headingSelectorValues.employeeType)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in openToMoveEmployeeTypeOptions" v-bind:key="option.text"
                    v-bind:value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

              <div slot="filter__contractType">
                <select class="form-control" v-model="headingSelectorValues.contractType"
                  @change="filterByColumn(openToMoveConfig.v2.columns.contractType, headingSelectorValues.contractType)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in openToMoveContractTypeOptions" :key="option.text" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

              <div slot="filter__lengthEmployment">
                <select class="form-control" v-model="headingSelectorValues.lengthEmployment"
                  @change="filterByColumn(openToMoveConfig.v2.columns.lengthEmployment, headingSelectorValues.lengthEmployment)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in lengthEmploymentOptions" :key="option.text" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

              <div slot="filter__languages">
                <select class="form-control" v-model="headingSelectorValues.languages"
                  @change="filterByColumn(openToMoveConfig.v2.columns.languages, headingSelectorValues.languages)">
                  <option value="">{{ $t('global.select') }} </option>
                  <option v-for="option in languageOptions" :key="option.text" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

            </v-client-table>
          </b-col>
        </b-row>

      </b-container>
      <template #modal-footer>
        <div class="ui-align-end">
          <button class="ui-cancel-button" @click="closeModalMassiveConfirmation()">
            {{ $t("global.cancel") }}
          </button>
          <button class="ui-action-button" @click="sendMassiveConfirmation()">
            {{ $t('global.confirm') }}
          </button>
        </div>
      </template>
    </b-modal>

  </div>
</template>
<script>

import Vue from "vue";
import JsonExcel from "vue-json-excel";

Vue.component("downloadExcel", JsonExcel);

import { required } from "vuelidate/lib/validators";

import API_SERVICE from "@/lib/api/OpenToMove.js";
import store from '@/vuex/store';

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

const api = new API_SERVICE();

import S3_Handler from "@/lib/api/s3_handler";
const s3 = new S3_Handler();

import {
  openToMoveStatus,
  openToMoveConfig,
  apiVersion,
  booleanOptions,
  openToMoveContractTypeOptions,
  openToMoveEmployeeTypeOptions,
  lengthEmploymentOptions,
  languageOptions,
  openToMoveLicenseType,
  openToMoveCSV
} from '@/lib/constants';

import { getFormattedDate, normalizeString } from '@/lib/utils';

export default {
  components: {
    store,
    DatePicker
  },
  data() {
    return {
      json_data: [],
      selectedItems: [],
      apiVersion,
      headingSelectorValues: {
        mobilityColumn: '',
        employeeType: '',
        contractType: '',
        licenseType: '',
        lengthEmployment: '',
        status: '',
        languages: ''
      },
      openToMoveConfig,
      booleanOptions,
      openToMoveContractTypeOptions,
      openToMoveEmployeeTypeOptions,
      lengthEmploymentOptions,
      languageOptions,
      currentItem: {},
      dates:
      {
        from: null,
        to: null,
      },
      statusList: openToMoveStatus,
      openToMoveLicenseType,
      json_meta: [
        [
          {
            key: "charset",
            value: "ISO-8859-1",
          },
        ],
      ],

    };
  },
  validations: {
    dates: {
      from: {
        required
      },
      to: {
        required
      }
    }
  },
  computed: {
    TableOpenToMove: function () {
      return this.$refs.TableOpenToMove;
    },
    getStatusObject: function () {
      return openToMoveStatus.reduce(
        (result, currentValue) => {
          result[currentValue.key] = currentValue.key
          return result
        }, {});
    },
    appLang: function () {
      return this.$store.getters['settings/currentLang'];
    },
    table_openToMove_columns: function () {
      return [
        openToMoveConfig.v1.columns.checkbox,
        openToMoveConfig.v1.columns.userName,
        openToMoveConfig.v1.columns.userLastName,
        openToMoveConfig.v1.columns.userMothersLastName,
        openToMoveConfig.v1.columns.userControlNumber,
        openToMoveConfig.v1.columns.createdAt,
        openToMoveConfig.v1.columns.ou,
        openToMoveConfig.v1.columns.ouSub,
        openToMoveConfig.v2.columns.currentExperience,
        openToMoveConfig.v2.columns.employeeType,
        openToMoveConfig.v2.columns.contractType,
        openToMoveConfig.v2.columns.contractEndDate,
        openToMoveConfig.v2.columns.lengthEmployment,
        openToMoveConfig.v2.columns.ouInterest,
        openToMoveConfig.v2.columns.ouSubInterest,
        openToMoveConfig.v2.columns.internationalMobility,
        openToMoveConfig.v1.columns.reasons,
        openToMoveConfig.v2.columns.languages,
        openToMoveConfig.v2.columns.softwareExperience,
        openToMoveConfig.v1.columns.license,
        openToMoveConfig.v1.columns.licenseType,
        openToMoveConfig.v1.columns.resume,
        openToMoveConfig.v1.columns.status,
        openToMoveConfig.v1.columns.action
      ]
    },
    getFilteredColumns: function () {
      const hiddenColumns = [openToMoveConfig.v1.columns.checkbox, openToMoveConfig.v1.columns.action];
      const columns = this.table_openToMove_columns.filter(column => !hiddenColumns.includes(column));
      return columns;
    },
    table_openToMove_headings: function () {
      if (this.table_openToMove_columns) {
        const headings = {};
        this.table_openToMove_columns.forEach(column => {
          headings[column] = this.$t(
            `openToMove.application.tableOpenToMove.headings.${column}`
          );
        });
        return headings;
      }
      return null;
    },
    table_filters_openToMove: function () {
      const disabledColumns = [
        openToMoveConfig.v1.columns.checkbox,
        openToMoveConfig.v1.columns.action,
        openToMoveConfig.v1.columns.resume,
        openToMoveConfig.v1.columns.license,
        openToMoveConfig.v1.columns.createdAt,
        openToMoveConfig.v2.columns.contractEndDate
      ];

      return this.table_openToMove_columns.filter(column => !disabledColumns.includes(column));
    },
    detailFields: function () {
      const allFields = [
        {
          key: openToMoveConfig.v1.columns.userName,
          columns: {
            v1: { sm: 2 },
            v2: { sm: 2 }
          }
        },
        {
          key: openToMoveConfig.v1.columns.userLastName,
          columns: {
            v1: { sm: 2 },
            v2: { sm: 2 }
          }
        },
        {
          key: openToMoveConfig.v1.columns.userMothersLastName,
          columns: {
            v1: { sm: 2 },
            v2: { sm: 2 }
          }
        },
        {
          key: openToMoveConfig.v1.columns.userControlNumber,
          columns: {
            v1: { sm: 2 },
            v2: { sm: 2 }
          }
        },
        {
          key: openToMoveConfig.v2.columns.employeeType,
          columns: {
            v2: { sm: 2 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.contractType,
          columns: {
            v2: { sm: 2 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.contractEndDate,
          columns: {
            v2: { sm: 2 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v1.columns.ou,
          columns: {
            v1: { sm: 3 },
            v2: { sm: 3 }
          }
        },
        {
          key: openToMoveConfig.v1.columns.ouSub,
          columns: {
            v1: { sm: 3 },
            v2: { sm: 3 }
          }
        },
        {
          key: openToMoveConfig.v2.columns.lengthEmployment,
          columns: {
            v2: { sm: 3 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.ouInterest,
          columns: {
            v2: { sm: 3 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.ouSubInterest,
          columns: {
            v2: { sm: 3 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.internationalMobility,
          columns: {
            v2: { sm: 3 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v2.columns.languages,
          columns: {
            v2: { sm: 3 }
          },
          version: apiVersion.v2
        },
        {
          key: openToMoveConfig.v1.columns.license,
          columns: {
            v1: { sm: 3 },
            v2: { sm: 3 }
          },
          type: 'link'
        },
        {
          key: openToMoveConfig.v1.columns.resume,
          columns: {
            v1: { sm: 3 },
            v2: { sm: 3 }
          },
          type: 'link'
        },
        {
          key: openToMoveConfig.v1.columns.reasons,
          columns: {
            v1: { sm: 12 },
            v2: { sm: 12 }
          },
          type: 'textarea',
        },
        {
          key: openToMoveConfig.v2.columns.currentExperience,
          columns: {
            v1: { sm: 12 },
            v2: { sm: 6 }
          },
          type: 'textarea'
        },
        {
          key: openToMoveConfig.v2.columns.softwareExperience,
          columns: {
            v1: { sm: 6 },
            v2: { sm: 6 }
          },
          type: 'textarea',
          version: apiVersion.v2
        }
      ];

      let fields = [...allFields];
      if (this.currentItem && this.currentItem.version === apiVersion.v1) {
        fields = allFields.filter(field => field.version !== apiVersion.v2);
      }

      return fields;
    },
    canChangeStatus: function () {
      return Boolean(
        this.currentItem.status === this.getStatusObject.seen && this.currentItem.originalStatus == this.getStatusObject.delivered
      )
    },
    json_fields: function () {
      return {
        [openToMoveCSV.id]: 'id',
        [openToMoveCSV.name]: {
          field: openToMoveConfig.v1.columns.userName,
          callback: (value) => {
            return normalizeString(value);
          },
        },
        [openToMoveCSV.lastName]: {
          field: openToMoveConfig.v1.columns.userLastName,
          callback: (value) => {
            return normalizeString(value);
          },
        },
        [openToMoveCSV.mothersLastName]: {
          field: openToMoveConfig.v1.columns.userMothersLastName,
          callback: (value) => {
            return normalizeString(value);
          },
        },
        [normalizeString(openToMoveCSV.controlNumber)]: openToMoveConfig.v1.columns.userControlNumber,
        [openToMoveCSV.createdAt]: {
          field: "createdAt",
          callback: (value) => {
            return getFormattedDate(value);
          },
        },
        [openToMoveCSV.experience]: {
          field: openToMoveConfig.v2.columns.currentExperience,
          callback: (value) => {
            if (value && value.length) {
              return this.getExperience(value);
            } else {
              return '';
            }
          }
        },
        [openToMoveCSV.licenseType]: {
          field: openToMoveConfig.v1.columns.licenseType,
          callback: (value) => {
            const license = openToMoveLicenseType.find(license => license.key === value);
            const licenseName = license && license.name ? license.name : '';
            return normalizeString(licenseName);
          }
        },
        [openToMoveCSV.ou]: openToMoveConfig.v1.columns.ou,
        [openToMoveCSV.ouSub]: openToMoveConfig.v1.columns.ouSub,
        [openToMoveCSV.reasons]: openToMoveConfig.v1.columns.reasons,
        [openToMoveCSV.status]: {
          field: openToMoveConfig.v1.columns.status,
          callback: (value) => {
            const status = openToMoveStatus.find(status => status.key === value);
            const statusName = status && status.name ? status.name : '';
            return statusName;
          }
        },
        [openToMoveCSV.contractType]: openToMoveConfig.v2.columns.contractType,
        [openToMoveCSV.contractEndDate]: {
          field: openToMoveConfig.v2.columns.contractEndDate,
          callback: (value) => {
            return getFormattedDate(value);
          }
        },
        [openToMoveCSV.lengthEmployment]: openToMoveConfig.v2.columns.lengthEmployment,
        [openToMoveCSV.ouSubInterest]: openToMoveConfig.v2.columns.ouSubInterest,
        [openToMoveCSV.employeeType]: openToMoveConfig.v2.columns.employeeType,
        [openToMoveCSV.ouInterest]: openToMoveConfig.v2.columns.ouInterest,
        [openToMoveCSV.internationalMobility]: {
          field: openToMoveConfig.v2.columns.internationalMobility,
          callback: (value) => {
            const stringValue = value ? this.$t('global.yes') : this.$t('global.no');
            return normalizeString(stringValue);
          }
        },
        [openToMoveCSV.languages]: {
          field: openToMoveConfig.v2.columns.languages,
          callback: (value) => {
            if (value && value.length) {
              return normalizeString(this.getLanguages(value));
            } else {
              return '';
            }
          }
        },
        [openToMoveCSV.softwareExperience]: {
          field: openToMoveConfig.v2.columns.softwareExperience,
          callback: (value) => {
            if (value && value.length) {
              const stringValue = value.toString();
              return normalizeString(stringValue);

            } else {
              return '';
            }
          }
        }
      }
    }
  },
  filters: {
    formatDateOnly: function (value) {
      if (!value) return '';
      return getFormattedDate(value)
    },
    formatContractEndDate: function (value) {
      if (!value) return '';
      return getFormattedDate(value);
    }
  },
  beforeMount() {
    this.createTable();
  },
  methods: {
    createTable: function () {
      this.table_open_to_move = {
        columns: this.table_openToMove_columns,
        columns2ndTable: this.getFilteredColumns,
        options: {
          columnsDropdown: true,
          filterByColumn: true,
          headings: this.table_openToMove_headings,
          filterable: this.table_filters_openToMove,
          sortable: ['createdAt', 'contractEndDate'],
          sortIcon: {
            base: "fas",
            up: "fa-sort-up",
            down: "fa-sort-down",
            is: "fa-sort",
          },
          orderBy: {
            ascending: false
          },
          resizableColumns: false,
          perPage: 25,
          perPageValues: [10, 25, 50, 100],
          texts: {
            filterBy: "{column}",
            columns: this.$t("bookingTransport.planning.TableStops.headings.column_toggler"),
            limit: this.$t("bookingTransport.planning.TableStops.headings.limit")
          },
          requestAdapter: function (data) {
            const queries = {};
            queries.pagination = {
              limit: data.limit,
              offset: data.limit * (data.page - 1),
            };
            queries.filter = data.query;
            if (data.orderBy === "contractEndDate" || data.orderBy === "createdAt") {
              queries.orderBy = data.orderBy;
            } else {
              queries.orderBy = "createdAt";
            }

            queries.orderType = data.ascending ? "asc" : "desc";
            return queries;
          },
          requestFunction: async function (data) {
            this.$store.commit("global/SET_LOADING", true);
            try {
              const response = await api.getListsOpenToMove(data);

              if (response && response.items) {
                for(let m=0;m<response.items.length;m++){
                  if(response.items[m].contractEndDate){
                    if(response.items[m].contractEndDate == "Fecha no definida"){
                      response.items[m].contractEndDate = "";
                    }
                  }
                }
                const filteredItems = response.items.map(item => {
                  if (item && item.version === apiVersion.v1) {
                    item.currentExperience = item.experience;
                  }

                  if (item && item.version === apiVersion.v2) {
                    item.softwareExperience = item.softwareExperience.toString();
                  }
                  return item;
                });
                response.items = [...filteredItems]
              }
              this.$store.commit("global/SET_LOADING", false);
              return response;
            } catch (error) {
              this.$store.commit("global/SET_LOADING", false);
              this.$store.commit("global/SET_ALERT", {
                msg: `${error}`,
                show: true,
                type: "error"
              });
              throw error;
            }
          },
          responseAdapter: function (response) {
            return {
              data: response.items,
              count: response.total
            };
          }
        }
      };
    },
    async fetchData() {
      this.$v.$touch();
      if (this.$v.$error) return;

      if (this.dates.to < this.dates.from) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: `${this.$t("mobilityOnDemand.rango")}`,
          show: true,
          type: "error"
        });
        return;
      }
      try {
        this.$store.commit("global/SET_LOADING", true);

        const getDate = (type = "from") => {
          const time = type === "from" ? "00:00 -06:00" : "23:59 +00:00";
          return `${this.dates[type]} ${time}`;

        };
        const params = {
          startDate: getDate("from"),
          endDate: getDate("to"),
        };

        const queries = {};
        queries.pagination = {
          limit: 10000,
          offset: 0,
        };
        queries.filter = {
          createdAtFrom: params.startDate,
          createdAtTo: params.endDate
        };

        const response = await api.getListsOpenToMove(queries);
        if (response && response.items) {
          for(let m=0;m<response.items.length;m++){
              if(response.items[m].contractEndDate){
                  if(response.items[m].contractEndDate == "Fecha no definida"){
                      response.items[m].contractEndDate = "";
                  }
              }
          }
          const mapItems = response.items.map(item => {
            if (item && item.version === apiVersion.v1) {
              item.currentExperience = item.experience;
            }
            if (item && item.version === apiVersion.v2) {
              item.softwareExperience = item.softwareExperience.toString();
            }
            return item;
          });
          response.items = mapItems;
        }

        this.json_data = response.items;
        this.$store.commit("global/SET_LOADING", false);
        return response.items;


      } catch (error) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: error,
          show: true,
          type: "error"
        });
      }
    },
    finishDownload() {
      this.$store.commit("global/SET_LOADING", false);
      this.$store.commit("global/SET_ALERT", {
        msg: "Descargado....",
        show: true,
        type: "success"
      });
    },
    sendMassiveConfirmation: async function () {
      try {
        this.$store.commit("global/SET_LOADING", true);
        for await (const data of this.selectedItems) {
          await this.updateStatus(data);
        }
        this.selectedItems = [];
        this.TableOpenToMove.refresh();
        this.$refs['my-notification-massive'].hide();
        this.$store.commit("global/SET_LOADING", false);
      } catch (error) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: error,
          show: true,
          type: "error"
        });
      }
    },
    closeModalMassiveConfirmation() {
      this.selectedItems = [];
      this.$refs['my-notification-massive'].hide();
    },
    openMassiveConfirmationModal: async function () {
      try {
        this.$refs['my-notification-massive'].show();
      } catch (error) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: error,
          show: true,
          type: "error"
        });
      }
    },
    async checkMassive(data, cb) {
      if (cb.target.checked) {
        this.selectedItems.push(data);
      }
      else {
        this.selectedItems = this.selectedItems.filter((e) => e !== data)
      }
    },
    filterByColumn: function (columnName, columnValue) {
      if (columnName === openToMoveConfig.v1.columns.experience) {
        columnName = openToMoveConfig.v2.columns.currentExperience;
      }
      this.TableOpenToMove.setFilter({ [columnName]: columnValue });
    },
    Download: async function (url) {
      try {
        if (url.trim() === '') {
          this.$store.commit("global/SET_LOADING", false);
          this.$store.commit("global/SET_ALERT", {
            msg: `${this.$t("openToMove.ErrorDowload")}`,
            show: true,
            type: "error"
          });
        }
        else {
          const resp = await s3.getSignedUrl(url);
          let anchor = document.createElement("a");
          anchor.href = resp;
          anchor.target = "_blank";
          anchor.click();
          this.$store.commit("global/SET_LOADING", false);
          this.$store.commit("global/SET_ALERT", {
            msg: `${this.$t("openToMove.SuccessDowload")}`,
            show: true,
            type: "success"
          });
        }
      }
      catch (error) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: error,
          show: true,
          type: "error"
        });
      }
    },
    changeStatus: async function () {
      try {
        this.$store.commit("global/SET_LOADING", true);
        if (this.canChangeStatus) {
          await this.updateStatus(this.currentItem);
        } else {
          this.$store.commit("global/SET_ALERT", {
            msg: `${this.$t("openToMove.ErrorState")}`,
            show: true,
            type: "ERROR"
          });
          this.$store.commit("global/SET_LOADING", false);
        }
      }
      catch (error) {
        this.$store.commit("global/SET_LOADING", false);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('openToMove.application.detail.statusError'),
          show: true,
          type: "error"
        });
      }
    },
    updateStatus: async function (requestData) {
      const queries = {};
      queries.status = this.getStatusObject.seen;
      try {
        const response = await api.ChangeState(queries, requestData.id);
        if (response.status == 200) {
          await this.sendNotification(requestData);
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('openToMove.application.detail.statusError'),
          show: true,
          type: "error"
        });
      }
    },
    sendNotification: async function (data) {
      const area = data.version === apiVersion.v1 ? data.ouSub : data.ouSubInterest;
      const queries = {
        type: "open-to-move",
        priority: "",
        controlNumbers: data.userControlNumber,
        title: "Open To Move",
        subtitle: "Recluting Center",
        cover: null,
        body: {
          ops: [
            {
              insert: `¡Hola, ${data.userName}! Hemos recibido tu solicitud de manera exitosa. \n Revisaremos tu perfil y, si cumples con los requisitos solicitados por el área ${area}, nos comunicaremos contigo. Si tienes alguna pregunta o comentario, con gusto estamos para apoyarte. \n Saludos. \n Recluting Center.`
            }
          ]
        },
        author: "Audi México"
      };
      try {
        const notificationResponse = await api.postNotif(queries);
        if (notificationResponse.status == 200) {
          this.$store.commit("global/SET_LOADING", false);
          this.$store.commit("global/SET_ALERT", {
            msg: `${this.$t("openToMove.updateNoti")}`,
            show: true,
            type: "success"
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('openToMove.application.detail.statusError'),
          show: true,
          type: "ERROR"
        });
      }
    },
    getStatusName: function (estatus) {
      const status = this.statusList.find(status => status.key === estatus);
      const statusName = status && status.name ? status.name : '';
      return statusName;
    },
    getLicenseType(licenseType) {
      const license = openToMoveLicenseType.find(license => license.key === licenseType);
      return license && license.name ? license.name : '';
    },
    getLanguages(languages = [], html = false) {
      let newLine = html ? '</br>' : `\n`;
      let languagesString = '';

      if (languages && languages.length && Array.isArray(languages)) {
        newLine = languages.length > 1 ? newLine : '';
        languages.forEach(language => {
          languagesString += `${language.name}: ${language.level}.${newLine}`;
        });
      }

      return languagesString;
    },
    getExperience(experience, html = false) {
      if (typeof experience === 'string') return experience;
      let newLine = html ? '</br>' : `\n`;
      let experienceString = '';
      if (experience && experience.length) {
        newLine = experience.length > 1 ? newLine : '';
        experience.forEach(currentExperience => {
          experienceString += `${currentExperience.ou} ${currentExperience.ouSub}${newLine}`;
        });
      }

      return experienceString;
    },
    closeEditRequest() {
      this.currentItem = {};
      this.$refs['Edit_Open_to_move'].hide();
    },
    openEditModal: async function (data) {
      this.currentItem = {};

      this.currentItem = Object.assign({}, data);
      this.currentItem.originalStatus = data.status;

      if (data.license && data.license.trim() !== "") {
        this.currentItem.license = await s3.getSignedUrl(data.license);
      }
      if (data.resume && data.resume.trim() !== "") {
        this.currentItem.resume = await s3.getSignedUrl(data.resume);
      }

      if (this.currentItem.version === apiVersion.v2) {
        this.currentItem.languages = this.getLanguages(data.languages);
        this.currentItem.softwareExperience = this.currentItem.softwareExperience.toString();
        this.currentItem.internationalMobility = data.internationalMobility ? this.$t('global.yes') : this.$t('global.no');
      }

      this.currentItem.currentExperience = this.getExperience(data.currentExperience);
      this.currentItem.licenseType = this.getLicenseType(data.licenseType);
      this.currentItem.parsedCreatedAt = getFormattedDate(data.createdAt);
      this.currentItem.contractEndDate = getFormattedDate(data.contractEndDate);


      this.$refs['Edit_Open_to_move'].show();
    }
  }
}
</script>

<style lang="scss" scoped>
.openToMove {
  display: flex;
  width: 100%;

  &__application-modal {
    &--title {
      max-width: 40%;
    }
  }
}

.wrapped-text {
  max-width: 200px;
  overflow-wrap: break-word;
  width: 100%
}
</style>